"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  LayoutDashboard,
  Boxes,
  Activity,
  Building,
  Users,
  Key,
  Settings,
  Printer,
  Crown,
  LogOut,
  Clock,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Scan,
  Zap,
  Tags,
  MapPin
} from "lucide-react";
import { clearSession, getStoredToken } from "../../lib/auth";
import { apiFetchJson } from "../../lib/api";
import { COPYRIGHT, FRONTEND_VERSION } from "../../components/VersionInfo";

type User = {
  id: number;
  email: string;
  role: string;
  is_superadmin: boolean;
};

type Plan = {
  id: number;
  name: string;
  max_users: number;
  max_locations: number;
  max_items: number;
  forecast_enabled: boolean;
  notifications_enabled: boolean;
  order_module_enabled: boolean;
};

type Subscription = {
  id: number;
  status: string;
  plan: Plan;
  trial_ends_at?: string;
};

type Tenant = {
  id: number;
  name: string;
  subscriptions?: Subscription[];
};

// Ticket #276: only a genuine platform admin (superadmin or
// role=='platform_admin') may see the cross-tenant "Alle" view. role=='admin'
// denotes a tenant-scoped admin in this app (e.g. jochmann_admin) and must
// stay confined to their own tenant(s).
export function isGenuinePlatformAdmin(user: Pick<User, 'is_superadmin' | 'role'>): boolean {
  return Boolean(user.is_superadmin || user.role === 'platform_admin');
}

export function resolveTenantSelection(
  user: Pick<User, 'is_superadmin' | 'role'>,
  tenantsFromApi: Tenant[],
  savedActiveTenantId: string | null
): { tenants: Tenant[]; initial: Tenant | null } {
  const isPlatformAdmin = isGenuinePlatformAdmin(user);
  const tenants = isPlatformAdmin
    ? [{ id: 'all' as any, name: 'Alle' }, ...tenantsFromApi]
    : tenantsFromApi;

  if (tenants.length === 0) return { tenants, initial: null };

  let initial: Tenant | null = null;
  if (savedActiveTenantId) {
    if (savedActiveTenantId === 'all') {
      // A stale "all" saved by a non-platform-admin (or from before this
      // fix) must never resolve to the cross-tenant view.
      initial = isPlatformAdmin ? (tenants.find(t => t.id === 'all' as any) || null) : null;
    } else {
      initial = tenants.find(t => t.id === parseInt(savedActiveTenantId)) || null;
    }
  }
  if (!initial) initial = tenants[0];
  return { tenants, initial };
}

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAdminOpen, setIsAdminOpen] = useState(true);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200); // 20 minutes in seconds

  useEffect(() => {
    setMounted(true);
    const token = getStoredToken();
    if (!token) {
      router.replace("/login");
      return;
    }

    // Timer logic
    const timerInterval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const resetTimer = () => setTimeLeft(1200);
    window.addEventListener("mousedown", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("scroll", resetTimer);
    window.addEventListener("touchstart", resetTimer);

    async function fetchUserInfo() {
      try {
        const u = await apiFetchJson<User>("/users/me");
        setUser(u);

        try {
          const fetchedTenants = await apiFetchJson<Tenant[]>("/me/tenants");
          const savedId = localStorage.getItem('activeTenantId');
          const { tenants, initial } = resolveTenantSelection(u, fetchedTenants, savedId);
          setTenants(tenants);
          if (initial) {
            setActiveTenant(initial);
            localStorage.setItem('activeTenantId', initial.id.toString());
          }
        } catch (e) {
          console.error("Failed to fetch tenants", e);
        } finally {
          setTenantLoading(false);
        }
      } catch (e) {
        console.error("Failed to fetch user info", e);
        clearSession();
        router.replace("/login");
      }
    }
    fetchUserInfo();

    return () => {
      clearInterval(timerInterval);
      window.removeEventListener("mousedown", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("scroll", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
    };
  }, [router]);

  if (!mounted) return null;

  function logout() {
    clearSession();
    router.replace("/login");
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const navItems = [
    { href: "/", label: "Dashboard", icon: LayoutDashboard },
    { href: "/consumables", label: "Verbrauchsmaterial", icon: Boxes },
    { href: "/scanner", label: "Scanner", icon: Scan },
    { href: "/billing", label: "Abrechnung", icon: CreditCard },
  ];

  const baseAdminItems = [
    { href: "/audit", label: "Audit Log", icon: Activity },
    { href: "/tenants", label: "Mandanten", icon: Building },
    { href: "/users", label: "Benutzer", icon: Users },
    { href: "/categories", label: "Kategorien", icon: Tags },
    { href: "/locations", label: "Lagerorte", icon: MapPin },
    { href: "/apikeys", label: "API Keys", icon: Key },
    { href: "/settings/print-profiles", label: "Druckprofile", icon: Printer },
    { href: "/settings", label: "Einstellungen", icon: Settings },
  ];

  const adminItems = user?.is_superadmin
    ? [
      ...baseAdminItems.slice(0, 3), // audit, tenants, users
      { href: "/plans", label: "Subscription Plans", icon: Crown },
      ...baseAdminItems.slice(3)     // apikeys, settings
    ]
    : baseAdminItems;


  return (
    <div className="flex min-h-screen bg-background font-sans">
      <aside className="w-64 bg-nav-bg text-white fixed left-0 top-0 bottom-0 flex flex-col z-30">
        {/* Scrollable Container */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {/* Logo Area */}
          <div className="pt-5 pb-4 flex items-center px-4">
            <img src="/logo_green.png" alt="Logo" className="h-18 w-auto object-contain block -ml-11" />
            <div className="-ml-5">
              <h1 className="text-xl font-bold tracking-tight leading-none text-white">Meditrack NG</h1>
              <p className="text-[10px] text-gray-400 mt-1">Tektena UG</p>
            </div>
          </div>

          {/* Tenant Switcher */}
          <div className="px-4 mb-6 relative">
            <div
              className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
              onClick={() => tenants.length > 1 && setTenantDropdownOpen(o => !o)}
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary shrink-0">
                  <Building size={16} />
                </div>
                <div className="overflow-hidden">
                  <p className="text-[9px] text-gray-400 uppercase font-bold tracking-wider leading-none mb-1">Mandant</p>
                  <p className="text-[13px] font-bold truncate leading-none">{activeTenant?.name || "Lade..."}</p>
                </div>
              </div>
              {tenants.length > 1 && (
                <ChevronDown
                  size={14}
                  className={`text-gray-400 transition-transform duration-200 shrink-0 ${tenantDropdownOpen ? 'rotate-180' : ''}`}
                />
              )}
            </div>

            {tenantDropdownOpen && tenants.length > 1 && (
              <div className="absolute left-4 right-4 top-full mt-1 bg-[#1a3a52] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                {tenants.map(t => (
                  <button
                    key={t.id}
                    onClick={() => {
                      localStorage.setItem('activeTenantId', t.id.toString());
                      setActiveTenant(t);
                      setTenantDropdownOpen(false);
                      // Reload so all data re-fetches with the new tenant header
                      window.location.reload();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${t.id === activeTenant?.id
                      ? 'bg-primary/20 text-white'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                      }`}
                  >
                    <Building size={14} className="shrink-0 opacity-60" />
                    <span className="text-[13px] font-bold truncate">{t.name}</span>
                    {t.id === activeTenant?.id && (
                      <span className="ml-auto text-[9px] font-black uppercase tracking-widest text-primary">Aktiv</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Plan Info removed as per user request */}

          {/* Navigation */}
          <div className="px-4 py-2 space-y-1">
            {navItems.map((item) => {
            const active = pathname === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${active
                  ? "bg-primary-soft text-primary font-bold shadow-sm"
                  : "text-gray-300 hover:bg-nav-bg-hover hover:text-white"
                  }`}
              >
                <item.icon size={20} className={active ? "text-primary" : "text-gray-400 opacity-60 group-hover:opacity-100"} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}

          <div className="pt-6">
            <div
              className="mb-2 px-4 flex items-center justify-between cursor-pointer py-1.5 rounded-lg text-gray-400 group hover:text-white transition-colors"
              onClick={() => setIsAdminOpen(!isAdminOpen)}
            >
              <span className="text-[11px] font-bold uppercase tracking-widest">{"Verwaltung"}</span>
              {isAdminOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>

            {isAdminOpen && (
              <div className="space-y-1">
                {adminItems.map((item) => {
                  const active = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${active
                        ? "bg-primary-soft text-primary font-bold shadow-sm"
                        : "text-gray-300 hover:bg-nav-bg-hover hover:text-white"
                        }`}
                    >
                      <item.icon size={18} className={active ? "text-primary" : "text-gray-500 group-hover:text-white transition-colors"} />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Footer */}
        <div className="px-3 py-3 bg-black/10 border-t border-white/10 mt-auto space-y-0.5">
          {user && (
            <Link
              href="/profile"
              className={`flex items-center justify-between px-2 py-2 mb-1 rounded-lg transition-all group ${pathname === '/profile' ? 'bg-white/10' : 'hover:bg-white/5'
                }`}
            >
              <div className="overflow-hidden">
                <p className="text-[11px] font-bold text-white uppercase tracking-tight leading-none group-hover:text-primary transition-colors">
                  {user.role?.replace('_', ' ') || 'Admin'}
                </p>
                <p className="text-[10px] text-gray-500 truncate max-w-[130px] mt-0.5" title={user.email}>{user.email}</p>
              </div>
              <div className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-gray-500 shrink-0">
                <Clock size={8} />
                <span>{formatTime(timeLeft)}</span>
              </div>
            </Link>
          )}

          <button
            onClick={logout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium text-red-400 hover:bg-red-400/10 hover:text-red-300 transition-all text-left"
          >
            <LogOut size={15} className="opacity-60 shrink-0" />
            Abmelden
          </button>

          <div className="text-[9px] text-gray-600 px-2 pt-2 flex justify-between items-center border-t border-white/5 mt-1">
            <span>{COPYRIGHT}</span>
            <span className="font-bold">v{FRONTEND_VERSION}</span>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-10 min-h-screen overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          {tenantLoading ? (
            <div className="flex flex-col items-center justify-center h-96 opacity-30 animate-pulse">
              <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
              <p className="font-semibold tracking-wider uppercase text-xs">Initialisierung...</p>
            </div>
          ) : (
            children
          )}
        </div>
      </main>
    </div>
  );
}
